import {Box, Button, Grid, Modal, Typography} from '@mui/material';
import React, {useCallback, useMemo, useState} from 'react';
import FormDateRange from '../shared/FormDateRange';
import FormCheckbox from '../shared/FormCheckbox';
import {SessionTypes} from '../../enums';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Player} from '../../interfaces';
import styles from '../../styles';
import {playersSort} from '../../services/teamService';

interface TeamPlayerSessionsReportForm {
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
    key: 'selection';
  } | null;
  profileIds: Record<string, boolean>;
  sessionTypes: Record<string, boolean>;
}

interface Props {
  players: Player[];
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  onDownload?: () => void;
  onReset?: () => void;
  onSubmit: ({
    startDate,
    endDate,
    profileIds,
    sessionTypes,
  }: {
    startDate: Date;
    endDate: Date;
    profileIds: number[];
    sessionTypes: string[];
  }) => void;
}

const TeamReportsFilter: React.FC<Props> = ({
  players,
  onDownload,
  onReset,
  onSubmit,
  defaultStartDate,
  defaultEndDate,
}) => {
  const {t} = useTranslation();

  const {handleSubmit, watch, reset, control} =
    useForm<TeamPlayerSessionsReportForm>({
      defaultValues: {
        dateRange:
          defaultStartDate && defaultEndDate
            ? {
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                key: 'selection',
              }
            : null,
        profileIds: {},
        sessionTypes: {},
      },
    });

  const sortedPlayers = useMemo(() => {
    return playersSort({players});
  }, [players]);

  const playerNames = useMemo(() => {
    const names = Object.keys(watch('profileIds'))
      .filter(profile => watch(`profileIds.${profile}`))
      .map(profile => {
        // find the player
        const player = players.find(
          player => player.profile?.id === parseInt(profile),
        );
        // if the player is found, return the name, otherwise return null
        if (player) {
          return `${player.profile?.firstName} ${player.profile?.lastName}`;
        }
        return null;
      })
      // filter out null values
      .filter(p => p)
      // join the names with a comma
      .join(', ');
    return names;
  }, [players, watch]);

  const [showPlayerModal, setShowPlayerModal] = useState<boolean>(false);

  const handleSubmitCallback = useCallback(
    async (data: TeamPlayerSessionsReportForm) => {
      const profileIds = Object.keys(data.profileIds)
        .filter(profile => data.profileIds[profile])
        .map(profile => parseInt(profile));
      const sessionTypes = Object.keys(data.sessionTypes).filter(
        sessionType => data.sessionTypes[sessionType],
      );

      onSubmit({
        startDate: data.dateRange?.startDate as Date,
        endDate: data.dateRange?.endDate as Date,
        profileIds,
        sessionTypes,
      });
    },
    [onSubmit],
  );

  const handleReset = useCallback(() => {
    reset();
    if (onReset) {
      onReset();
    }
    // submit the form
    handleSubmit(handleSubmitCallback)();
  }, [reset, onReset, handleSubmit, handleSubmitCallback]);

  return (
    <form onSubmit={handleSubmit(handleSubmitCallback)}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormDateRange
            label={t('sessionFilter.sessionDates') as string}
            control={control as unknown as Control<FieldValues>}
            name="dateRange"
            sx={{mb: 2}}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* TODO FIXME this is duplicated */}
          <Box sx={{mb: 2, position: 'relative', top: -10}}>
            <Typography variant="caption" color="text.secondary">
              {t('sessionFilter.sessionTypes')}
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap'}}>
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.game}`}
                label={`${t('sessionFilter.game')}`}
                sx={{mr: 2}}
              />
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.practice}`}
                label={`${t('sessionFilter.practice')}`}
                sx={{mr: 2}}
              />
              <FormCheckbox
                control={control as unknown as Control<FieldValues>}
                name={`sessionTypes.${SessionTypes.skills}`}
                label={`${t('sessionFilter.skills')}`}
                sx={{mr: 2}}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Button sx={{mb: 1}} onClick={() => setShowPlayerModal(true)}>
        {t('sessionFilter.selectPlayers')}
      </Button>
      <Typography variant="body1" sx={{mb: 2}}>
        {playerNames.length > 0 ? playerNames : t('sessionFilter.allPlayers')}
      </Typography>
      <Modal open={showPlayerModal} onClose={() => setShowPlayerModal(false)}>
        <Box sx={styles.modal}>
          <Typography variant="h2" sx={{mb: 2}}>
            {t('general.players')}
          </Typography>
          <Grid container>
            {(sortedPlayers ?? []).map(player => (
              <Grid item xs={12} md={4} key={player?.profile?.id} sx={{px: 1}}>
                <FormCheckbox
                  control={control as unknown as Control<FieldValues>}
                  name={`profileIds.${player.profile?.id}`}
                  label={`${player.profile?.firstName} ${player.profile?.lastName}${player.profile?.position ? ` - ${t(`position.${player.profile?.position}`)}` : ''}`}
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
            <Button
              onClick={() => {
                setShowPlayerModal(false);
                // submit the form
                handleSubmit(handleSubmitCallback)();
              }}
              variant="contained">
              {t('general.apply')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}>
        <Box>
          <Button variant="outlined" onClick={handleReset} sx={{mr: 2}}>
            {t('general.reset')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!watch('dateRange')}>
            {t('general.submit')}
          </Button>
        </Box>
        {onDownload && (
          <Button variant="outlined" onClick={onDownload}>
            {t('general.download')}
          </Button>
        )}
      </Box>
    </form>
  );
};

export default TeamReportsFilter;
